import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PlanillaCabecera, PlanillonData } from '../types/planillon';
import { PlanillonService } from '../services';
import type { PeriodoPlanilla } from '../../periodos/types/periodo';
import { PeriodoService } from '../../periodos/services';

interface PlanillonState {
  // Data
  planillas: PlanillaCabecera[];
  periodos: PeriodoPlanilla[];
  planillonData: PlanillonData | null;
  selectedPlanilla: PlanillaCabecera | null;
  selectedPeriodoId: number | null;

  // UI
  loading: boolean;
  calculating: boolean;
  error: string | null;
  dialogOpen: boolean;

  // Actions
  fetchPeriodos: () => Promise<void>;
  fetchPlanillas: () => Promise<void>;
  fetchPlanillasByPeriodo: (idPeriodo: number) => Promise<void>;
  fetchPlanillon: (idPlanilla: number) => Promise<void>;
  createPlanilla: (planilla: PlanillaCabecera) => Promise<void>;
  deletePlanilla: (id: number) => Promise<void>;
  generarDetalle: (idPlanilla: number) => Promise<void>;
  calcularPlanilla: (idPlanilla: number) => Promise<void>;
  cambiarEstado: (idPlanilla: number, nuevoEstado: string) => Promise<void>;
  setSelectedPeriodoId: (id: number | null) => void;
  setSelectedPlanilla: (planilla: PlanillaCabecera | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const usePlanillonStore = create<PlanillonState>()(
  devtools(
    (set, get) => ({
      // Initial state
      planillas: [],
      periodos: [],
      planillonData: null,
      selectedPlanilla: null,
      selectedPeriodoId: null,
      loading: false,
      calculating: false,
      error: null,
      dialogOpen: false,

      // ── Fetch periodos ──────────────────────────
      fetchPeriodos: async () => {
        try {
          const periodos = await PeriodoService.getAll();
          set({ periodos }, false, 'planillon/fetchPeriodos/success');
        } catch (error) {
          console.error('Error cargando periodos:', error);
        }
      },

      // ── Fetch planillas ─────────────────────────
      fetchPlanillas: async () => {
        set({ loading: true, error: null }, false, 'planillon/fetchPlanillas/start');
        try {
          const planillas = await PlanillonService.getAll();
          set({ planillas, loading: false }, false, 'planillon/fetchPlanillas/success');
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al cargar planillas' },
            false,
            'planillon/fetchPlanillas/error'
          );
        }
      },

      fetchPlanillasByPeriodo: async (idPeriodo: number) => {
        set({ loading: true, error: null, selectedPeriodoId: idPeriodo }, false, 'planillon/fetchByPeriodo/start');
        try {
          const planillas = await PlanillonService.getByPeriodo(idPeriodo);
          set({ planillas, loading: false }, false, 'planillon/fetchByPeriodo/success');
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al cargar planillas' },
            false,
            'planillon/fetchByPeriodo/error'
          );
        }
      },

      // ── Fetch planillón ─────────────────────────
      fetchPlanillon: async (idPlanilla: number) => {
        set({ loading: true, error: null }, false, 'planillon/fetchPlanillon/start');
        try {
          const planillonData = await PlanillonService.getPlanillon(idPlanilla);
          set({ planillonData, loading: false }, false, 'planillon/fetchPlanillon/success');
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al cargar planillón' },
            false,
            'planillon/fetchPlanillon/error'
          );
        }
      },

      // ── Crear planilla ──────────────────────────
      createPlanilla: async (planilla: PlanillaCabecera) => {
        set({ loading: true, error: null }, false, 'planillon/create/start');
        try {
          // Solo enviar campos necesarios - el backend asigna FechaCreacion, Estado, totales
          const payload = {
            idPeriodo: planilla.idPeriodo,
            descripcion: planilla.descripcion,
            tipoPlanilla: planilla.tipoPlanilla,
          };
          const created = await PlanillonService.create(payload as PlanillaCabecera);
          set(
            (state) => ({
              planillas: [created, ...state.planillas],
              loading: false,
              dialogOpen: false,
              selectedPlanilla: created,
            }),
            false,
            'planillon/create/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al crear planilla');
          set({ loading: false, error: errorMessage }, false, 'planillon/create/error');
          throw error;
        }
      },

      // ── Eliminar planilla ───────────────────────
      deletePlanilla: async (id: number) => {
        set({ loading: true, error: null }, false, 'planillon/delete/start');
        try {
          await PlanillonService.delete(id);
          set(
            (state) => ({
              planillas: state.planillas.filter((p) => p.idPlanilla !== id),
              loading: false,
              planillonData: state.planillonData?.planilla.idPlanilla === id ? null : state.planillonData,
              selectedPlanilla: state.selectedPlanilla?.idPlanilla === id ? null : state.selectedPlanilla,
            }),
            false,
            'planillon/delete/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al eliminar planilla');
          set({ loading: false, error: errorMessage }, false, 'planillon/delete/error');
        }
      },

      // ── Generar detalle ─────────────────────────
      generarDetalle: async (idPlanilla: number) => {
        set({ calculating: true, error: null }, false, 'planillon/generarDetalle/start');
        try {
          const updated = await PlanillonService.generarDetalle(idPlanilla);
          set(
            (state) => ({
              planillas: state.planillas.map((p) => (p.idPlanilla === idPlanilla ? updated : p)),
              selectedPlanilla: updated,
              calculating: false,
            }),
            false,
            'planillon/generarDetalle/success'
          );
          // Recargar planillón
          get().fetchPlanillon(idPlanilla);
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al generar detalle');
          set({ calculating: false, error: errorMessage }, false, 'planillon/generarDetalle/error');
        }
      },

      // ── Calcular planilla ───────────────────────
      calcularPlanilla: async (idPlanilla: number) => {
        set({ calculating: true, error: null }, false, 'planillon/calcular/start');
        try {
          const updated = await PlanillonService.calcular(idPlanilla);
          set(
            (state) => ({
              planillas: state.planillas.map((p) => (p.idPlanilla === idPlanilla ? updated : p)),
              selectedPlanilla: updated,
              calculating: false,
            }),
            false,
            'planillon/calcular/success'
          );
          // Recargar planillón con datos calculados
          get().fetchPlanillon(idPlanilla);
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al calcular planilla');
          set({ calculating: false, error: errorMessage }, false, 'planillon/calcular/error');
        }
      },

      // ── Cambiar estado ─────────────────────────
      cambiarEstado: async (idPlanilla: number, nuevoEstado: string) => {
        set({ loading: true, error: null }, false, 'planillon/cambiarEstado/start');
        try {
          const updated = await PlanillonService.cambiarEstado(idPlanilla, nuevoEstado);
          set(
            (state) => ({
              planillas: state.planillas.map((p) => (p.idPlanilla === idPlanilla ? updated : p)),
              selectedPlanilla: updated,
              loading: false,
            }),
            false,
            'planillon/cambiarEstado/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al cambiar estado');
          set({ loading: false, error: errorMessage }, false, 'planillon/cambiarEstado/error');
        }
      },

      // ── UI mutations ────────────────────────────
      setSelectedPeriodoId: (id) => set({ selectedPeriodoId: id }, false, 'planillon/setSelectedPeriodoId'),
      setSelectedPlanilla: (planilla) => set({ selectedPlanilla: planilla }, false, 'planillon/setSelectedPlanilla'),
      openDialog: () => set({ dialogOpen: true }, false, 'planillon/openDialog'),
      closeDialog: () => set({ dialogOpen: false }, false, 'planillon/closeDialog'),
      clearError: () => set({ error: null }, false, 'planillon/clearError'),
    }),
    { name: 'planillon-store' }
  )
);
